/**
 * "Get the Android app": a banner for Android browsers, and the install sheet
 * it (and the More tab) opens. The sheet explains Android's sideloading
 * warnings, because a bare APK link plus a scary system dialog loses people —
 * the same reason Lift has its /get page.
 */

import { useState } from 'react'

import { APK_URL, shouldShowBanner } from '../lib/androidPrompt'
import { isNative } from '../native/platform'
import { Sheet } from './Sheet'

const DISMISS_KEY = 'macro.androidBanner.dismissedAt'

function readDismissed(): number | null {
  try {
    const v = localStorage.getItem(DISMISS_KEY)
    return v ? Number(v) : null
  } catch { return null }
}

export function AndroidBanner({ hasApp }: { hasApp: boolean }) {
  const [dismissedAt, setDismissedAt] = useState(readDismissed)
  const [open, setOpen] = useState(false)
  const show = shouldShowBanner({ ua: navigator.userAgent, native: isNative(), dismissedAt, now: Date.now(), hasApp })
  if (!show && !open) return null
  const dismiss = () => {
    const now = Date.now()
    try { localStorage.setItem(DISMISS_KEY, String(now)) } catch { /* private mode: shows again next visit */ }
    setDismissedAt(now)
  }
  return (
    <>
      {show && (
        <div style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', padding: '.6rem 1rem',
          display: 'flex', alignItems: 'center', gap: '.6rem' }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: '.88rem' }}>
            <b>Get the Macro app for Android</b>
            <div className="muted small">Works offline and opens straight to your log.</div>
          </div>
          <button className="secondary" style={{ marginTop: 0, minHeight: 36, padding: '.3rem .7rem' }} onClick={dismiss}>Not now</button>
          <button style={{ marginTop: 0, minHeight: 36, padding: '.3rem .8rem' }} onClick={() => setOpen(true)}>Get it</button>
        </div>
      )}
      {open && <InstallSheet onClose={() => { setOpen(false); dismiss() }} />}
    </>
  )
}

export function InstallSheet({ onClose }: { onClose: () => void }) {
  return (
    <Sheet title="Macro for Android" onClose={onClose}>
      <p style={{ marginTop: '.75rem' }}>Macro isn't in the Play Store — it installs straight from this site, and updates itself from inside the app.</p>
      <a href={APK_URL} download style={{ display: 'block', textDecoration: 'none' }}>
        <button style={{ width: '100%', marginTop: '.25rem' }}>Download Macro</button>
      </a>
      <ol className="small" style={{ paddingLeft: '1.2rem', lineHeight: 1.6 }}>
        <li>Open the download. If Android says your browser isn't allowed to install apps, tap <b>Settings</b>, turn on <b>Allow from this source</b>, and go back.</li>
        <li>Tap <b>Install</b>. Play Protect may say it doesn't recognise the app — that's because it isn't from the Play Store. Choose <b>Install anyway</b>.</li>
        <li>Open Macro and sign in with your Lift account. Everything you've logged here is already there.</li>
      </ol>
      <p className="muted small">When it asks for the camera, that's for barcode scanning and meal photos.</p>
    </Sheet>
  )
}

/** The More tab's entry. */
export function AndroidAppCard({ entry, hasApp }: { entry: 'install' | 'link'; hasApp: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="card">
      {entry === 'install' ? (
        <>
          <p className="muted small" style={{ marginTop: 0, marginBottom: 0 }}>
            {hasApp ? 'You already use the app on an Android phone. Install it on another one:'
              : 'Works offline and updates itself. Sign in with the same Lift account.'}
          </p>
          <button onClick={() => setOpen(true)}>Get the Android app</button>
        </>
      ) : (
        <p className="muted small" style={{ margin: 0 }}>
          On an Android phone, open <a href={APK_URL}>macro.npcforge.ca{APK_URL}</a> to install it.
          (There's no iPhone app — on an iPhone, add this site to your Home Screen.)
        </p>
      )}
      {open && <InstallSheet onClose={() => setOpen(false)} />}
    </div>
  )
}
